import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type { ServerStatusResponse } from '@shared/types';
import { api } from '@/lib/api';
import { useConfig } from '@/lib/session';
import { Wordmark } from './Header';
import { SocialButtons } from './Social';
import { Dot } from './ui';

const COLUMNS: Array<{ title: string; links: Array<{ label: string; to: string }> }> = [
  {
    title: 'Play',
    links: [
      { label: 'Download', to: '/download' },
      { label: 'Create account', to: '/register' },
      { label: 'News & patch notes', to: '/news' },
      { label: 'Server guide', to: '/guide' },
      { label: 'Job change guide', to: '/guide/jobs' },
      { label: 'Vote for rewards', to: '/vote' },
    ],
  },
  {
    title: 'World',
    links: [
      { label: 'Rankings', to: '/rankings' },
      { label: 'Who is online', to: '/online' },
      { label: 'War of Emperium', to: '/woe' },
      { label: 'Hall of Fame', to: '/hall-of-fame' },
      { label: 'MVP tracker', to: '/mvp' },
      { label: 'Market', to: '/market' },
      { label: 'Database', to: '/database' },
    ],
  },
  {
    title: 'Support the game',
    links: [
      { label: 'Buy donation points', to: '/donate' },
      { label: 'Cash shop items', to: '/database/cash-shop' },
      { label: 'Official resellers', to: '/resellers' },
      { label: 'Become a partner', to: '/partners' },
    ],
  },
  {
    title: 'Rules',
    links: [
      { label: 'Server rules', to: '/legal/rules' },
      { label: 'Terms of service', to: '/legal/terms' },
      { label: 'Privacy', to: '/legal/privacy' },
      { label: 'Refund policy', to: '/legal/refunds' },
      { label: 'Partner terms', to: '/legal/partners' },
    ],
  },
];

export function Footer() {
  const config = useConfig();
  const status = useQuery({ queryKey: ['status'], queryFn: () => api<ServerStatusResponse>('/status'), refetchInterval: 30_000 });
  const up = !!status.data && status.data.login && status.data.char && status.data.map;
  return (
    <footer className="relative z-10 mt-24 border-t border-line bg-scrim/60">
      <div className="mx-auto grid max-w-7xl grid-cols-2 gap-x-6 gap-y-10 px-4 py-14 sm:px-6 lg:grid-cols-[1.3fr_repeat(4,1fr)]">
        <div className="col-span-2 lg:col-span-1">
          <Link to="/" className="inline-flex items-center gap-2.5">
            <img src="/img/logo-mark.png" alt="" width={34} height={34} />
            <Wordmark />
          </Link>
          <p className="mt-4 max-w-xs text-sm leading-relaxed text-dim">{config.tagline}</p>
          <SocialButtons size="sm" className="mt-5" />
        </div>
        {COLUMNS.map((col) => (
          <div key={col.title}>
            <p className="eyebrow mb-4">{col.title}</p>
            <ul className="space-y-2.5 text-sm">
              {col.links.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-muted transition-colors hover:text-ink">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-line/70">
        <div className="mx-auto flex max-w-7xl flex-col gap-2 px-4 py-5 text-xs text-dim sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <p>&copy; {new Date().getFullYear()} {config.serverName}. A fan-made classic MMORPG; not affiliated with any other game publisher.</p>
          <p className="flex items-center gap-2">
            <Dot on={up} />
            {status.data ? (up ? `Servers online · ${status.data.inWorld.toLocaleString()} online` : 'Servers offline') : 'Checking servers...'}
          </p>
        </div>
      </div>
    </footer>
  );
}
